'use client';

import { useMemo, useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'motion/react';
import { type RestaurantLocale } from '@restaurant/config';
import styles from './HeroLogoShowcase.module.css';

interface HeroLogoShowcaseProps {
  locale?: RestaurantLocale;
  alt?: string;
}

const LOCALIZED_TEXTS = {
  fr: {
    circleText: '✦ LE B BRASSERIE ✦ RESTAURANT & BRASSERIE ✦ MAISON FRANÇAISE ✦ SAVOIR-FAIRE ✦ ',
  },
  en: {
    circleText: '✦ LE B BRASSERIE ✦ RESTAURANT & BRASSERIE ✦ FRENCH DINING ✦ ART OF LIVING ✦ ',
  },
  es: {
    circleText: '✦ LE B BRASSERIE ✦ RESTAURANTE & BRASERÍA ✦ CASA FRANCESA ✦ ARTE DE VIVIR ✦ ',
  },
};

const PARTICLES = [
  { id: 1, top: '18%', left: '20%', size: 4, delay: '0s', duration: '5.6s', driftX: '18px', driftY: '-55px' },
  { id: 2, top: '26%', left: '78%', size: 3.5, delay: '1.2s', duration: '6.4s', driftX: '-22px', driftY: '-65px' },
  { id: 3, top: '68%', left: '14%', size: 4.5, delay: '0.6s', duration: '6.0s', driftX: '14px', driftY: '-50px' },
  { id: 4, top: '76%', left: '80%', size: 3, delay: '2.0s', duration: '6.8s', driftX: '-16px', driftY: '-60px' },
  { id: 5, top: '40%', left: '86%', size: 3.5, delay: '3.2s', duration: '5.8s', driftX: '-20px', driftY: '-58px' },
  { id: 6, top: '82%', left: '44%', size: 4, delay: '2.4s', duration: '6.2s', driftX: '12px', driftY: '-52px' },
];

export function HeroLogoShowcase({ locale = 'fr', alt = 'Le B Brasserie' }: HeroLogoShowcaseProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const content = LOCALIZED_TEXTS[locale] ?? LOCALIZED_TEXTS.fr;

  // Normalized cursor coordinates: -0.5 to 0.5
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth luxury spring physics
  const springConfig = { stiffness: 120, damping: 22, mass: 0.8 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // 3D rotations based on smoothed cursor
  const rotateX = useTransform(smoothY, [-0.5, 0.5], [10, -10]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-12, 12]);

  // Sheen light displacement tracking cursor smoothly
  const sheenX = useTransform(smoothX, [-0.5, 0.5], [-45, 45]);
  const sheenY = useTransform(smoothY, [-0.5, 0.5], [-45, 45]);

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (reduced) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handlePointerLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Generate 60 precision gauge ticks
  const gaugeTicks = useMemo(() => {
    const count = 60;
    const radius = 216;
    const center = 250;
    return Array.from({ length: count }, (_, i) => {
      const angle = (i * 360) / count;
      const rad = (angle * Math.PI) / 180;
      const isMajor = i % 15 === 0;
      const isMedium = i % 5 === 0;
      const length = isMajor ? 9 : isMedium ? 6 : 3.5;
      const r1 = radius;
      const r2 = radius - length;
      return {
        id: i,
        x1: center + r1 * Math.cos(rad),
        y1: center + r1 * Math.sin(rad),
        x2: center + r2 * Math.cos(rad),
        y2: center + r2 * Math.sin(rad),
        isMajor,
      };
    });
  }, []);

  return (
    <div
      ref={containerRef}
      className={styles.showcaseWrapper}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {/* Ambient background glow */}
      <div className={styles.ambientGlow} aria-hidden="true" />

      {/* Floating champagne particles */}
      {mounted &&
        !reduced &&
        PARTICLES.map((p) => (
          <span
            key={p.id}
            className={styles.emberParticle}
            style={
              {
                top: p.top,
                left: p.left,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animationDelay: p.delay,
                animationDuration: p.duration,
                '--drift-x': p.driftX,
                '--drift-y': p.driftY,
              } as React.CSSProperties
            }
            aria-hidden="true"
          />
        ))}

      {/* Main 3D tilting stage */}
      <motion.div
        className={styles.stage3D}
        style={
          mounted && !reduced
            ? {
                rotateX,
                rotateY,
              }
            : undefined
        }
      >
        {/* Concentric Craftsmanship SVG Rings */}
        <svg viewBox="0 0 500 500" className={styles.svgContainer} aria-hidden="true">
          <defs>
            {/* Circular path for rotating typography */}
            <path
              id="heroLogoCirclePath"
              d="M 250, 250 m -186, 0 a 186,186 0 1,1 372,0 a 186,186 0 1,1 -372,0"
              fill="none"
            />
          </defs>

          {/* Precision gauge ticks */}
          <g>
            {gaugeTicks.map((tick) => (
              <line
                key={tick.id}
                x1={tick.x1}
                y1={tick.y1}
                x2={tick.x2}
                y2={tick.y2}
                className={tick.isMajor ? styles.tickLineMajor : styles.tickLine}
              />
            ))}
          </g>

          {/* Outer fine border ring */}
          <circle cx="250" cy="250" r="236" className={styles.outerRing} />

          {/* Counter-rotating delicate dashed ring */}
          <g className={styles.counterRotatingGroup}>
            <circle cx="250" cy="250" r="226" className={styles.dashedRing} />
            {/* 4 Cardinal Diamond Stars */}
            <path
              d="M 250 21 L 253 24 L 250 27 L 247 24 Z"
              className={styles.cardinalDiamond}
            />
            <path
              d="M 479 250 L 476 253 L 473 250 L 476 247 Z"
              className={styles.cardinalDiamond}
            />
            <path
              d="M 250 479 L 253 476 L 250 473 L 247 476 Z"
              className={styles.cardinalDiamond}
            />
            <path
              d="M 21 250 L 24 253 L 27 250 L 24 247 Z"
              className={styles.cardinalDiamond}
            />
          </g>

          {/* Ribbon track framing rings */}
          <circle cx="250" cy="250" r="206" className={styles.outerRing} />
          <circle cx="250" cy="250" r="166" className={styles.outerRing} />

          {/* Rotating arched typography ribbon */}
          <g className={styles.rotatingGroup}>
            <text className={styles.curvedText} textLength="1140" lengthAdjust="spacing">
              <textPath href="#heroLogoCirclePath" xlinkHref="#heroLogoCirclePath" startOffset="0%">
                {content.circleText}
              </textPath>
            </text>
          </g>
        </svg>

        {/* Frosted porcelain / crystal pedestal */}
        <div className={styles.pedestal}>
          {/* Double inner luxury rims */}
          <div className={styles.pedestalInnerBorder} aria-hidden="true" />
          <div className={styles.pedestalInnerRing} aria-hidden="true" />

          {/* Specular sheen gliding across the pedestal */}
          <motion.div
            className={styles.specularSheen}
            style={
              mounted && !reduced
                ? {
                    x: sheenX,
                    y: sheenY,
                  }
                : undefined
            }
            aria-hidden="true"
          />

          {/* Central authentic logo */}
          <div className={styles.logoWrapper}>
            <Image
              src="/logo_Site_remove.webp"
              alt={alt}
              width={500}
              height={500}
              priority
              className={styles.logoImage}
              sizes="(max-width: 560px) 240px, (max-width: 900px) 290px, 360px"
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
export default HeroLogoShowcase;
